<template>
  <div class="class-student-statistics-page">
    <!-- 顶部校区选择 -->
    <div :class="isPC ? 'header-section-pc' :'header-section'">
      <div v-if="currentOrgName" class="org-title">{{ currentOrgName }}</div>
      <div class="date-range" v-if="dateRangeDisplay">{{ dateRangeDisplay }}</div>
      <!-- <div class="org-select-card">
        <div class="org-select-btn" @click="showOrgPicker = true">
          {{ selectedOrgName }} <van-icon name="arrow-down" />
        </div>
      </div> -->
    </div>

    <!-- 校区选择底部弹层 -->
    <!-- <van-action-sheet
      v-model:show="showOrgPicker"
      :actions="orgOptions.map(item => ({ name: item.text, value: item.value }))"
      cancel-text="取消"
      @select="handleOrgSelect"
    /> -->

    <!-- 人数概览 -->
    <div class="overview-section">
      <div class="overview-header">
        <div class="section-title">
          <span class="title-icon"></span>
          人数概览
        </div>
      </div>
      <div class="overview-cards">
        <!-- <div class="overview-card total">
          <div class="card-content">
            <div class="card-value">{{ overviewData.totalClassCount }}</div>
            <div class="card-label">总班级</div>
          </div>
        </div> -->
        <div class="overview-card total">
          <div class="card-content">
            <div class="card-value">{{ overviewData.totalStudentCount }}</div>
            <div class="card-label">总人数</div>
          </div>
        </div>
        <div class="overview-card primary">
          <div class="card-content">
            <div class="card-value">{{ overviewData.primaryStudentCount }}</div>
            <div class="card-label">小学人数</div>
          </div>
        </div>
        <div class="overview-card junior">
          <div class="card-content">
            <div class="card-value">{{ overviewData.juniorStudentCount }}</div>
            <div class="card-label">初中人数</div>
          </div>
        </div>
        <div class="overview-card senior">
          <div class="card-content">
            <div class="card-value">{{ overviewData.seniorStudentCount }}</div>
            <div class="card-label">高中人数</div>
          </div>
        </div>
      </div>
    </div>

    <!-- 科目人数统计（学段堆叠图） -->
    <div class="chart-section">
      <div class="section-title">
        <span class="title-icon subject"></span>
        科目人数统计
      </div>
      <div ref="subjectChart" class="bar-chart"></div>
    </div>

    <!-- 年级人数统计（堆叠图） -->
    <div class="chart-section">
      <div class="section-title">
        <span class="title-icon grade"></span>
        年级人数统计
      </div>
      <div ref="gradeChart" class="bar-chart"></div>
    </div>

    <!-- 班级人数列表 -->
    <div class="list-section">
      <div class="section-title-row">
        <div class="section-title">
          <span class="title-icon class-icon"></span>
          班级人数列表
        </div>
      </div>
      <div class="list-content">
        <template v-if="classList.length > 0">
          <div class="list-item header">
            <span class="col-class-name">班级</span>
            <span class="col-class-code">班级编号</span>
            <span class="col-count">人数</span>
          </div>
          <div
            v-for="(item, index) in classList"
            :key="index"
            class="list-item"
            :class="{ odd: index % 2 === 1 }"
          >
            <span class="col-class-name">{{ item.className }}</span>
            <span class="col-class-code">{{ item.classCode }}</span>
            <span class="col-count highlight">{{ item.studentCount || 0 }}</span>
          </div>
        </template>
        <EmptyData v-else tips="暂无班级人数数据" />
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, computed, onUnmounted, onBeforeUnmount } from 'vue'
import * as echarts from 'echarts'
import EmptyData from '@/components/EmptyData.vue'
import { getClassEnrollmentStatistics } from '@/api/revenue'
import { postDataPermissionsGetOrgs } from '@/api/report'
import { useRoute } from 'vue-router'

const PC_CHART_MIN_WIDTH = 768

// PC端判断：屏幕宽度 >= 768px
const isPC = ref(
  typeof window !== 'undefined' && window.innerWidth >= PC_CHART_MIN_WIDTH
)

// 校区选择缓存 key
const ORG_CACHE_KEY = 'classStudentStatistics_selectedOrg'

const selectedOrg = ref('all')
const orgOptions = ref([])

const startDate = ref('')
const endDate = ref('')
const currentOrgName = ref('')

const route = useRoute()

// 校区选择
const showOrgPicker = ref(false)

const selectedOrgName = computed(() => {
  const option = orgOptions.value.find(item => item.value == selectedOrg.value)
  return option?.text || '所有校区'
})

const handleOrgSelect = ({ value }) => {
  selectedOrg.value = value
  showOrgPicker.value = false
  loadData()
}

// 判断是否选择了所有校区
const isAllOrg = computed(() => selectedOrg.value === 'all')

/**
 * 多校区时 subject/grade 列表项会带 orgId；单校区整包通常只有根级 data.orgId，子项无 orgId。
 * 若仍用 item.orgId 过滤，会得到空数组，图表与概览学段人数全部为 0。
 */
const statsBelongToQueryOrg = (list, rootOrgId, queryOrgId) => {
  const arr = list || []
  const hasItemOrg = arr.some(item => item.orgId != null && item.orgId !== '')
  if (!hasItemOrg) {
    if (queryOrgId == null || queryOrgId === '') return arr
    if (rootOrgId != null && String(rootOrgId) !== String(queryOrgId)) return []
    return arr
  }
  if (queryOrgId == null || queryOrgId === '') return arr
  return arr.filter(item => String(item.orgId) === String(queryOrgId))
}

/** 按年级汇总学段人数（与 totalStudentCount 一致，不重复计跨科学生） */
const periodCountsFromGradeStats = gradeStats => {
  let primary = 0
  let junior = 0
  let senior = 0
  ;(gradeStats || []).forEach(item => {
    const g = parseInt(item.grade, 10)
    const n = Number(item.studentCount) || 0
    if (g >= 1 && g <= 6) primary += n
    else if (g >= 7 && g <= 9) junior += n
    else if (!Number.isNaN(g) && g >= 10) senior += n
  })
  return { primary, junior, senior }
}

/** 无年级数据时兜底：按科目 periodDetails 累加（可能跨科重复计人次） */
const periodCountsFromSubjectPeriodDetails = subjectStats => {
  let primary = 0
  let junior = 0
  let senior = 0
  ;(subjectStats || []).forEach(item => {
    ;(item.periodDetails || []).forEach(p => {
      const c = Number(p.studentCount) || 0
      if (p.period === '1') primary += c
      else if (p.period === '2') junior += c
      else if (p.period === '3') senior += c
    })
  })
  return { primary, junior, senior }
}

const mapSubjectRowsFromPeriodDetails = subjectStats =>
  (subjectStats || []).map(item => {
    const primaryItem = item.periodDetails?.find(p => p.period === '1')
    const juniorItem = item.periodDetails?.find(p => p.period === '2')
    const seniorItem = item.periodDetails?.find(p => p.period === '3')
    return {
      subjectName: item.subjectName || '未知科目',
      primaryCount: primaryItem?.studentCount || 0,
      juniorCount: juniorItem?.studentCount || 0,
      seniorCount: seniorItem?.studentCount || 0
    }
  })

// 人数概览数据
const overviewData = ref({
  totalStudentCount: 0,
  primaryStudentCount: 0,
  juniorStudentCount: 0,
  seniorStudentCount: 0,
  totalClassCount: 0
})

// 科目统计数据
const subjectData = ref([])

// 年级统计数据
const gradeData = ref([])

// 班级人数列表数据
const classList = ref([])

// 图表DOM引用
const subjectChart = ref(null)
const gradeChart = ref(null)
let subjectChartInstance = null
let gradeChartInstance = null

// 科目图例选中状态
const subjectLegendSelected = ref({ 小学: true, 初中: true, 高中: true })

// 日期范围显示
const dateRangeDisplay = computed(() => {
  if (startDate.value && endDate.value) {
    return `${startDate.value} 至 ${endDate.value}`
  }
  return ''
})

const getSubjectVisibleTotal = (row, sel) => {
  const p = Number(row.primaryCount) || 0
  const j = Number(row.juniorCount) || 0
  const s = Number(row.seniorCount) || 0
  let sum = 0
  if (sel['小学'] !== false) sum += p
  if (sel['初中'] !== false) sum += j
  if (sel['高中'] !== false) sum += s
  return sum
}

const getSubjectTopSegment = (row, sel) => {
  const p = Number(row.primaryCount) || 0
  const j = Number(row.juniorCount) || 0
  const s = Number(row.seniorCount) || 0
  const showS = sel['高中'] !== false && s > 0
  const showJ = sel['初中'] !== false && j > 0
  const showP = sel['小学'] !== false && p > 0
  if (showS) return 'senior'
  if (showJ) return 'junior'
  if (showP) return 'primary'
  return null
}

const subjectAllOrgTotalLabelFormatter = (layer) => (params) => {
  const row = subjectData.value[params.dataIndex]
  if (!row) return ''
  const sel = subjectLegendSelected.value
  const sum = getSubjectVisibleTotal(row, sel)
  if (!sum) return ''
  const top = getSubjectTopSegment(row, sel)
  if (layer === 'senior') return top === 'senior' ? String(sum) : ''
  if (layer === 'junior') return top === 'junior' ? String(sum) : ''
  if (layer === 'primary') return top === 'primary' ? String(sum) : ''
  return ''
}

const onSubjectLegendSelectChanged = (e) => {
  if (!subjectChartInstance) return
  subjectLegendSelected.value = { ...subjectLegendSelected.value, ...(e.selected || {}) }
  subjectChartInstance.setOption(
    {
      series: [
        { label: { formatter: subjectAllOrgTotalLabelFormatter('primary') } },
        { label: { formatter: subjectAllOrgTotalLabelFormatter('junior') } },
        { label: { formatter: subjectAllOrgTotalLabelFormatter('senior') } }
      ]
    },
    false
  )
}

// 年级图例选中状态
const gradeLegendSelected = ref({ 小学: true, 初中: true, 高中: true })

const getGradeVisibleTotal = (row, sel) => {
  const p = Number(row.primaryCount) || 0
  const j = Number(row.juniorCount) || 0
  const s = Number(row.seniorCount) || 0
  let sum = 0
  if (sel['小学'] !== false) sum += p
  if (sel['初中'] !== false) sum += j
  if (sel['高中'] !== false) sum += s
  return sum
}

const getGradeTopSegment = (row, sel) => {
  const p = Number(row.primaryCount) || 0
  const j = Number(row.juniorCount) || 0
  const s = Number(row.seniorCount) || 0
  const showS = sel['高中'] !== false && s > 0
  const showJ = sel['初中'] !== false && j > 0
  const showP = sel['小学'] !== false && p > 0
  if (showS) return 'senior'
  if (showJ) return 'junior'
  if (showP) return 'primary'
  return null
}

const gradeAllOrgTotalLabelFormatter = (layer) => (params) => {
  const row = gradeData.value[params.dataIndex]
  if (!row) return ''
  const sel = gradeLegendSelected.value
  const sum = getGradeVisibleTotal(row, sel)
  if (!sum) return ''
  const top = getGradeTopSegment(row, sel)
  if (layer === 'senior') return top === 'senior' ? String(sum) : ''
  if (layer === 'junior') return top === 'junior' ? String(sum) : ''
  if (layer === 'primary') return top === 'primary' ? String(sum) : ''
  return ''
}

const onGradeLegendSelectChanged = (e) => {
  if (!gradeChartInstance) return
  gradeLegendSelected.value = { ...gradeLegendSelected.value, ...(e.selected || {}) }
  gradeChartInstance.setOption(
    {
      series: [
        { label: { formatter: gradeAllOrgTotalLabelFormatter('primary') } },
        { label: { formatter: gradeAllOrgTotalLabelFormatter('junior') } },
        { label: { formatter: gradeAllOrgTotalLabelFormatter('senior') } }
      ]
    },
    false
  )
}

// 渲染科目人数统计图表（X 轴学科、Y 轴人数，小学/初中/高中堆叠；单校区与多校区一致）
const renderSubjectChart = () => {
  if (!subjectChart.value) return

  if (!subjectChartInstance) {
    subjectChartInstance = echarts.init(subjectChart.value)
    subjectChartInstance.on('legendselectchanged', onSubjectLegendSelectChanged)
  }

  const option = {
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow' },
      backgroundColor: 'rgba(255, 255, 255, 0.95)',
      borderColor: '#e8e8e8',
      borderWidth: 1,
      textStyle: { color: '#333' }
    },
    legend: {
      data: ['小学', '初中', '高中'],
      selected: { ...subjectLegendSelected.value },
      bottom: 5,
      itemWidth: 14,
      itemHeight: 14,
      textStyle: { fontSize: 12, color: '#666' },
      itemGap: 15
    },
    grid: {
      left: '3%',
      right: '4%',
      bottom: '18%',
      top: '8%',
      containLabel: true
    },
    xAxis: {
      type: 'category',
      data: subjectData.value.map(item => item.subjectName),
      axisLabel: { fontSize: 12, color: '#666' },
      axisLine: { lineStyle: { color: '#e8e8e8' } },
      axisTick: { show: false }
    },
    yAxis: {
      type: 'value',
      name: '',
      nameTextStyle: { color: '#999', fontSize: 11 },
      axisLabel: { fontSize: 11, color: '#999' },
      axisLine: { show: false },
      splitLine: { lineStyle: { color: '#f0f0f0', type: 'dashed' } }
    },
    series: [
      {
        name: '小学',
        type: 'bar',
        stack: 'total',
        barWidth: isPC.value ? 40 : '35%',
        data: subjectData.value.map(item => item.primaryCount),
        itemStyle: { color: '#52c41a', borderRadius: [0, 0, 4, 4] },
        label: {
          show: true,
          position: 'top',
          formatter: subjectAllOrgTotalLabelFormatter('primary'),
          fontSize: 12,
          fontWeight: 600,
          color: '#333'
        }
      },
      {
        name: '初中',
        type: 'bar',
        stack: 'total',
        barWidth: isPC.value ? 40 : '35%',
        data: subjectData.value.map(item => item.juniorCount),
        itemStyle: { color: '#faad14', borderRadius: [0, 0, 0, 0] },
        label: {
          show: true,
          position: 'top',
          formatter: subjectAllOrgTotalLabelFormatter('junior'),
          fontSize: 12,
          fontWeight: 600,
          color: '#333'
        }
      },
      {
        name: '高中',
        type: 'bar',
        stack: 'total',
        barWidth: isPC.value ? 40 : '35%',
        data: subjectData.value.map(item => item.seniorCount),
        itemStyle: { color: '#f5222d', borderRadius: [4, 4, 0, 0] },
        label: {
          show: true,
          position: 'top',
          formatter: subjectAllOrgTotalLabelFormatter('senior'),
          fontSize: 12,
          fontWeight: 600,
          color: '#333'
        }
      }
    ]
  }

  subjectChartInstance.setOption(option, { notMerge: true })
}

// 渲染年级人数统计（X 轴 gradeName、Y 轴人数，小学/初中/高中堆叠；单校区与多校区一致）
const renderGradeChart = () => {
  if (!gradeChart.value) return

  if (!gradeChartInstance) {
    gradeChartInstance = echarts.init(gradeChart.value)
    gradeChartInstance.on('legendselectchanged', onGradeLegendSelectChanged)
  }

  const option = {
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow' },
      backgroundColor: 'rgba(255, 255, 255, 0.95)',
      borderColor: '#e8e8e8',
      borderWidth: 1,
      textStyle: { color: '#333' }
    },
    legend: {
      data: ['小学', '初中', '高中'],
      selected: { ...gradeLegendSelected.value },
      bottom: 5,
      itemWidth: 14,
      itemHeight: 14,
      textStyle: { fontSize: 12, color: '#666' },
      itemGap: 15
    },
    grid: {
      left: '3%',
      right: '4%',
      bottom: '18%',
      top: '8%',
      containLabel: true
    },
    xAxis: {
      type: 'category',
      data: gradeData.value.map(item => item.gradeName),
      axisLabel: { fontSize: 12, color: '#666' },
      axisLine: { lineStyle: { color: '#e8e8e8' } },
      axisTick: { show: false }
    },
    yAxis: {
      type: 'value',
      name: '',
      nameTextStyle: { color: '#999', fontSize: 11 },
      axisLabel: { fontSize: 11, color: '#999' },
      axisLine: { show: false },
      splitLine: { lineStyle: { color: '#f0f0f0', type: 'dashed' } }
    },
    series: [
      {
        name: '小学',
        type: 'bar',
        stack: 'total',
        barWidth: isPC.value ? 40 : '35%',
        data: gradeData.value.map(item => item.primaryCount),
        itemStyle: { color: '#52c41a', borderRadius: [0, 0, 4, 4] },
        label: {
          show: true,
          position: 'top',
          formatter: gradeAllOrgTotalLabelFormatter('primary'),
          fontSize: 12,
          fontWeight: 600,
          color: '#333'
        }
      },
      {
        name: '初中',
        type: 'bar',
        stack: 'total',
        barWidth: isPC.value ? 40 : '35%',
        data: gradeData.value.map(item => item.juniorCount),
        itemStyle: { color: '#faad14', borderRadius: [0, 0, 0, 0] },
        label: {
          show: true,
          position: 'top',
          formatter: gradeAllOrgTotalLabelFormatter('junior'),
          fontSize: 12,
          fontWeight: 600,
          color: '#333'
        }
      },
      {
        name: '高中',
        type: 'bar',
        stack: 'total',
        barWidth: isPC.value ? 40 : '35%',
        data: gradeData.value.map(item => item.seniorCount),
        itemStyle: { color: '#f5222d', borderRadius: [4, 4, 0, 0] },
        label: {
          show: true,
          position: 'top',
          formatter: gradeAllOrgTotalLabelFormatter('senior'),
          fontSize: 12,
          fontWeight: 600,
          color: '#333'
        }
      }
    ]
  }

  gradeChartInstance.setOption(option, { notMerge: true })
}

// 渲染所有图表
const renderCharts = () => {
  renderSubjectChart()
  renderGradeChart()
}

// 窗口大小变化时重新渲染图表
const handleResize = () => {
  const prevPc = isPC.value
  isPC.value =
    typeof window !== 'undefined' && window.innerWidth >= PC_CHART_MIN_WIDTH
  subjectChartInstance?.resize()
  gradeChartInstance?.resize()
}

// 获取当前选中的校区ID
const getOrgId = () => {
  return selectedOrg.value === 'all' ? '' : selectedOrg.value
}

// 加载校区列表
const loadOrgList = async () => {
  try {
    const res = await postDataPermissionsGetOrgs()
    if (res.code === '00' && res.data && res.data.length > 0) {
      const options = res.data.map(item => ({
        text: item.name || item.orgName,
        value: item.id
      }))
      orgOptions.value = [
        { text: '所有校区', value: 'all' },
        ...options
      ]
    }
    const cachedOrg = sessionStorage.getItem(ORG_CACHE_KEY)
    if (cachedOrg && orgOptions.value.some(item => item.value == cachedOrg)) {
      selectedOrg.value = cachedOrg
    }
  } catch (error) {
    console.error('加载校区列表失败:', error)
    orgOptions.value = [
      { text: '所有校区', value: 'all' },
    ]
    const cachedOrg = sessionStorage.getItem(ORG_CACHE_KEY)
    if (cachedOrg && orgOptions.value.some(item => item.value == cachedOrg)) {
      selectedOrg.value = cachedOrg
    }
  }
}

// 加载数据
const loadData = async () => {
  // const orgId = getOrgId()
  const orgId = getQueryParam('creatOrg')

  try {
    const res = await getClassEnrollmentStatistics({ orgId, startDate: startDate.value, endDate: endDate.value  })
    console.log('班课人数统计数据返回:', res)

    if (res.code === '00' && res.data) {
      const data = res.data

      const subjectListForView = isAllOrg.value
        ? data.subjectStatistics || []
        : statsBelongToQueryOrg(data.subjectStatistics || [], data.orgId, orgId)
      const gradeListForView = isAllOrg.value
        ? data.gradeStatistics || []
        : statsBelongToQueryOrg(data.gradeStatistics || [], data.orgId, orgId)

      const hasExplicitPeriodOverview =
        data.primaryStudentCount != null &&
        data.juniorStudentCount != null &&
        data.seniorStudentCount != null

      let primaryStudentCount = 0
      let juniorStudentCount = 0
      let seniorStudentCount = 0
      if (hasExplicitPeriodOverview) {
        primaryStudentCount = Number(data.primaryStudentCount) || 0
        juniorStudentCount = Number(data.juniorStudentCount) || 0
        seniorStudentCount = Number(data.seniorStudentCount) || 0
      } else {
        const fromGrades = periodCountsFromGradeStats(gradeListForView)
        if (gradeListForView.length) {
          primaryStudentCount = fromGrades.primary
          juniorStudentCount = fromGrades.junior
          seniorStudentCount = fromGrades.senior
        } else {
          const fromSubjects = periodCountsFromSubjectPeriodDetails(subjectListForView)
          primaryStudentCount = fromSubjects.primary
          juniorStudentCount = fromSubjects.junior
          seniorStudentCount = fromSubjects.senior
        }
      }

      overviewData.value = {
        totalStudentCount: data.totalStudentCount || 0,
        primaryStudentCount,
        juniorStudentCount,
        seniorStudentCount,
        totalClassCount: data.totalClassCount || 0,
      }

      classList.value = data.classStatistics || data.classStudentStatistics || []

      subjectData.value = mapSubjectRowsFromPeriodDetails(subjectListForView)

      gradeData.value = (gradeListForView || []).map(item => {
        const grade = parseInt(item.grade, 10) || 0
        let primaryCount = 0
        let juniorCount = 0
        let seniorCount = 0
        if (grade >= 1 && grade <= 6) primaryCount = item.studentCount || 0
        else if (grade >= 7 && grade <= 9) juniorCount = item.studentCount || 0
        else if (grade >= 10) seniorCount = item.studentCount || 0
        return {
          gradeName: item.gradeName || '未知年级',
          primaryCount,
          juniorCount,
          seniorCount
        }
      })

      renderCharts()
    } else {
      console.warn('班课人数统计接口返回数据格式异常:', res)
      overviewData.value = {
        totalStudentCount: 0,
        primaryStudentCount: 0,
        juniorStudentCount: 0,
        seniorStudentCount: 0,
        totalClassCount: 0
      }
      subjectData.value = []
      gradeData.value = []
      classList.value = []
      renderCharts()
    }
  } catch (error) {
    console.error('加载班课人数统计数据失败:', error)
    overviewData.value = {
      totalStudentCount: 0,
      primaryStudentCount: 0,
      juniorStudentCount: 0,
      seniorStudentCount: 0,
      totalClassCount: 0
    }
    subjectData.value = []
    gradeData.value = []
    classList.value = []
    renderCharts()
  }
}

// iframe消息处理：接收web端导出参数请求
const handleIframeMessage = (event) => {
  const { data } = event
  if (data && data.type === 'getExportParams') {
    // 获取当前选中的参数
    // ClassStudentStatistics页面使用URL参数和selectedOrg状态
    const orgId = selectedOrg.value === 'all' ? '' : selectedOrg.value
    const startDate = startDate.value || ''
    const endDate = endDate.value || ''

    // 向web端返回参数
    if (event.source && event.source.postMessage) {
      event.source.postMessage(
        {
          type: 'exportParams',
          orgId,
          startDate,
          endDate
        },
        '*'
      )
    }
  }
}

const getQueryParam = (name) => {
  return route.query[name]
}

onMounted(async () => {
  startDate.value = getQueryParam('startDate')
  endDate.value = getQueryParam('endDate')
  selectedOrg.value = getQueryParam('creatOrg')
  currentOrgName.value = decodeURIComponent(getQueryParam('orgName') || '')
  // await loadOrgList()
  loadData()
  window.addEventListener('resize', handleResize)
  // 添加iframe消息监听
  window.addEventListener('message', handleIframeMessage)
})

onBeforeUnmount(() => {
  sessionStorage.setItem(ORG_CACHE_KEY, selectedOrg.value)
})

onUnmounted(() => {
  window.removeEventListener('resize', handleResize)
  subjectChartInstance?.dispose()
  gradeChartInstance?.dispose()
  // 清理iframe消息监听
  window.removeEventListener('message', handleIframeMessage)
})
</script>

<style lang="scss" scoped>
.class-student-statistics-page {
  background: linear-gradient(180deg, #e8f4ff 0%, #f5f6fa 30%);
  min-height: 100vh;
  padding-bottom: 20px;
  height: 100vh;
  overflow-y: scroll;
}

.header-section-pc {
  padding: 8px 16px 24px;

  .org-title {
    color: #333;
    font-size: 18px;
    font-weight: 600;
    text-align: left;
    padding-top: 8px;
    margin-bottom: 8px;
  }
}

.header-section {
  background: linear-gradient(135deg, #3478f6 0%, #5a9fff 100%);
  padding: 16px 16px 28px;
  border-radius: 0 0 24px 24px;
  box-shadow: 0 4px 16px rgba(52, 120, 246, 0.2);
  text-align: center;

  .org-title {
    color: #fff;
    font-size: 18px;
    font-weight: 600;
    margin-bottom: 4px;
  }

  .date-range {
    color: rgba(255, 255, 255, 0.85);
    font-size: 13px;
  }

  .org-select-card {
    display: flex;
    align-items: center;
  }

  .org-select-btn {
    color: #fff;
    font-size: 17px;
    font-weight: 600;
    padding: 0 12px;
    height: 40px;
    display: flex;
    align-items: center;
    cursor: pointer;
    background: transparent;
  }
}

.overview-section {
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(52, 120, 246, 0.08);
  margin: -22px 10px 12px 10px;
  padding: 12px;
  position: relative;
  z-index: 10;
}

.overview-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.section-title {
  font-size: 17px;
  font-weight: 600;
  color: #1a1a1a;
  display: flex;
  align-items: center;
  gap: 8px;

  .title-icon {
    width: 4px;
    height: 16px;
    background: linear-gradient(180deg, #3478f6 0%, #5a9fff 100%);
    border-radius: 2px;

    &.subject {
      background: linear-gradient(180deg, #722ed1 0%, #b37feb 100%);
    }

    &.grade {
      background: linear-gradient(180deg, #fa8c16 0%, #fdba74 100%);
    }

    &.class-icon {
      background: linear-gradient(180deg, #13c2c2 0%, #36cfc9 100%);
    }
  }
}

.overview-cards {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 8px;
}

.overview-card {
  background: linear-gradient(135deg, #f8faff 0%, #fff 100%);
  border-radius: 14px;
  padding: 14px 8px;
  text-align: center;
  border: 1px solid #e8eef5;
  transition: all 0.3s ease;

  &:active {
    transform: scale(0.98);
  }

  .card-content {
    display: flex;
    flex-direction: column;
    align-items: center;
  }

  .card-label {
    font-size: 11px;
    color: #8c8c8c;
    margin-bottom: 4px;
    white-space: nowrap;
  }

  .card-value {
    font-size: 20px;
    font-weight: 700;
    background: linear-gradient(135deg, #3478f6 0%, #5a9fff 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }

  &.primary .card-value {
    background: linear-gradient(135deg, #52c41a 0%, #95de64 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }

  &.junior .card-value {
    background: linear-gradient(135deg, #faad14 0%, #ffc53d 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }

  &.senior .card-value {
    background: linear-gradient(135deg, #f5222d 0%, #ff7875 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }
}

.chart-section {
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(52, 120, 246, 0.08);
  margin: 0 10px 12px 10px;
  padding: 12px;
}

.bar-chart {
  width: 100%;
  height: 240px;
}

.list-section {
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(52, 120, 246, 0.08);
  margin: 0 10px 12px 10px;
  padding: 12px;
}

.section-title-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.list-content {
  margin-top: 16px;
}

.list-item {
  display: flex;
  align-items: center;
  padding: 14px 12px;
  border-radius: 8px;
  font-size: 14px;

  &.header {
    background: #f5f7fa;
    color: #666;
    font-weight: 600;
    font-size: 13px;
    padding: 12px;
  }

  &.odd {
    background: #fafafa;
  }
}

.col-class-name {
  flex: 1.2;
  color: #333;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.col-class-code {
  flex: 1;
  color: #666;
  font-size: 13px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.col-count {
  flex: 0 0 50px;
  text-align: center;
  color: #666;

  &.highlight {
    color: #3478f6;
    font-weight: 600;
    font-size: 15px;
  }
}
</style>
